import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import widgetSchemas from 'shared/widgets/schemas';
import { flashMessage } from '~/app/actions';
import { diffPages, DiffComponent, SectionChange, SectionStatus } from '~/lib/pageDiff';
import { sectionElements, slotSelector } from '~/lib/sectionElements';
import { describeComponent, SLOT_LABELS } from '~/pages/components/PageEditor/shell/outline';
import { PAGE_SLOT_FIELDS } from '~/pages/components/PageEditor/composers/pageEditorGraphql';
import { LIGHT } from 'shared/lib/adminTheme';
import { S, TONES, DARK, labelStyle } from '~/pages/components/PageEditor/shell/shellTokens';

const REVIEW_PAGE = gql`
  query ReviewPage($id: ID!) {
    page(id: $id) { _id title path state publishedVersion commitedVersion canUserApprove canUserDecide }
    widgetPresets { _id title widgetId props }
    pageHistory(pageId: $id, limit: 20) { kind at who version text }
  }
`;

const REVIEW_VERSION = gql`
  query ReviewVersion($pageId: ID!, $versionId: String!) {
    pageVersion(pageId: $pageId, versionId: $versionId) {
      versionId
      notes
      updatedBy
      template { templateId version props }
      slots { ${PAGE_SLOT_FIELDS} }
    }
  }
`;

const APPROVE = gql`
  mutation ReviewApprove($id: ID!, $version: String) { approvePublishPage(pageId: $id, version: $version) { status message } }
`;
const DECLINE = gql`
  mutation ReviewDecline($id: ID!, $reason: String, $version: String) { declinePublishPage(pageId: $id, reason: $reason, version: $version) { status message } }
`;
const REFETCH = ['getPagesPendingApproval', 'getNavData', 'PageHistory'];

// `frame` marks the section inside the page frame, which is the live site (always light, no admin variables).
const STATUS: Record<SectionStatus, { label: string; color: string; frame: string }> = {
  added: { label: 'Added', color: S.green, frame: LIGHT.green },
  changed: { label: 'Changed', color: S.amber, frame: LIGHT.amber },
  moved: { label: 'Moved', color: '#5B6470', frame: '#5B6470' },
  removed: { label: 'Removed', color: S.red, frame: LIGHT.red }
};

type Version = { versionId: string; notes?: string | null; slots: Array<{ slotId: string; components: Array<DiffComponent | null> }>; template: Record<string, any> }; // eslint-disable-line @typescript-eslint/no-explicit-any
type View = 'side' | 'after' | 'before';

const DESKTOP = 1280;
// Marked in each frame: the requested version shows what it adds or changes, the live one what goes.
const AFTER: SectionStatus[] = ['added', 'changed', 'moved'];
const BEFORE: SectionStatus[] = ['removed'];
const MARKS_ID = 'gx-review-marks';

const cssString = (s: string) => `"${s.replace(/["\\]/g, '\\$&').replace(/\n/g, '\\A ')}"`;

/**
 * The changed sections of a frame, as CSS. A slot's sections are its div's children, so each rule picks
 * child i of exactly n: a slot whose children don't line up with its sections matches nothing. CSS applies
 * as soon as the page's HTML arrives (no waiting for its images and scripts) and stays when the page's own
 * scripts re-render it.
 */
const marksCss = (changes: SectionChange[], version: Version, label: (c: SectionChange) => string) => changes.map((ch) => {
  const slot = version.slots.find(sl => sl.slotId === ch.slotId);
  if (!slot || !slot.components.every(Boolean)) return '';
  const at = `${slotSelector(ch.slotId)}>:nth-child(${ch.index + 1}):nth-last-child(${slot.components.length - ch.index})`;
  const color = STATUS[ch.status].frame;
  // Sized for the frame's scale (about half): readable when shrunk.
  return `${at}{outline:6px solid ${color};outline-offset:-6px;position:relative}`
    + `${at}::after{content:${cssString(label(ch))};position:absolute;top:0;left:0;z-index:2147483647;padding:6px 14px;font:600 22px/1.3 "IBM Plex Mono",monospace;color:#fff;background:${color}}`;
}).join('');

/** Adds the marks to the page in a frame once its HTML starts arriving; false while the frame is still blank. */
const injectMarks = (frame: HTMLIFrameElement | null, css: string) => {
  const doc = frame && frame.contentDocument;
  if (!doc || !doc.head || doc.location.href === 'about:blank') return false;
  if (!doc.getElementById(MARKS_ID)) {
    const style = doc.createElement('style');
    style.id = MARKS_ID;
    style.textContent = css;
    doc.head.appendChild(style);
  }
  return true;
};

/** A section's element in a frame, when its slot's elements line up with the version's sections. */
const sectionIn = (frame: HTMLIFrameElement | null, version: Version | undefined, slotId: string, index: number) => {
  const doc = frame && frame.contentDocument;
  const slot = version && version.slots.find(sl => sl.slotId === slotId);
  const elements = doc && slot && slot.components.every(Boolean) ? sectionElements(doc, slotId, slot.components.length) : null;
  return elements ? elements[index] || null : null;
};

const useStyles = createUseStyles({
  root: { display: 'grid', gridTemplateColumns: '360px minmax(0, 1fr)', height: '100vh', fontFamily: S.ui, color: S.fg, background: S.wall },
  side: { display: 'flex', flexDirection: 'column', minHeight: 0, background: S.chrome, borderRight: `1px solid ${S.line}` },
  sideBody: { flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'grid', gap: 20, alignContent: 'start' },
  back: { fontSize: 13, color: S.muted, textDecoration: 'none', '&:hover': { color: S.fg } },
  title: { margin: '6px 0 2px', fontSize: 20, fontWeight: 600 },
  path: { fontFamily: S.mono, fontSize: 12, color: S.muted },
  versions: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontFamily: S.mono, fontSize: 13 },
  label: { ...labelStyle, marginBottom: 8 },
  note: { margin: 0, padding: '10px 12px', borderRadius: 6, background: S.field, border: `1px solid ${S.line}`, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  meta: { fontSize: 12, color: S.muted, marginTop: 6 },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 },
  change: {
    all: 'unset', boxSizing: 'border-box', width: '100%', cursor: 'pointer', display: 'grid', gridTemplateColumns: '78px minmax(0, 1fr) auto', gap: 10, alignItems: 'center', padding: '6px 8px', borderRadius: 6, fontSize: 14,
    '&:hover, &:focus-visible': { background: S.chromeHover },
    // The slate "moved" color is set inline; too dim on the dark panel.
    [DARK]: {
      '&[data-status="moved"] $status': { color: `${TONES.gray.fg} !important` },
      '&[data-status="moved"] $dot': { background: `${TONES.gray.dot} !important` }
    }
  },
  // A new page's sections: nothing to compare, so no status column.
  plain: { gridTemplateColumns: 'minmax(0, 1fr) auto' },
  status: { fontFamily: S.label, fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 },
  dot: { width: 7, height: 7, borderRadius: '50%' },
  name: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  where: { fontSize: 12, color: S.muted },
  muted: { margin: 0, fontSize: 13, color: S.muted, lineHeight: 1.5 },
  actions: { display: 'grid', gap: 8, padding: '14px 20px 18px', borderTop: `1px solid ${S.line}` },
  textarea: { boxSizing: 'border-box', width: '100%', padding: '8px 10px', fontFamily: S.ui, fontSize: 14, border: `1px solid ${S.fieldLine}`, borderRadius: 6, background: S.field, resize: 'vertical', '&:focus': { outline: 'none', borderColor: S.fg } },
  primary: { all: 'unset', boxSizing: 'border-box', cursor: 'pointer', textAlign: 'center', padding: '10px 14px', borderRadius: 6, fontSize: 14, fontWeight: 600, background: S.fg, color: S.field, '&[disabled]': { opacity: 0.4, cursor: 'default' } },
  danger: { all: 'unset', boxSizing: 'border-box', cursor: 'pointer', textAlign: 'center', padding: '9px 14px', borderRadius: 6, fontSize: 14, fontWeight: 600, border: `1px solid ${S.red}`, color: S.red, background: S.field, '&[disabled]': { opacity: 0.4, cursor: 'default' } },
  error: { margin: 0, padding: '8px 10px', borderRadius: 6, background: '#FBE9E9', color: S.red, fontSize: 13, [DARK]: { background: TONES.red.bg } },
  main: { display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 },
  bar: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: S.chrome, borderBottom: `1px solid ${S.line}`, fontSize: 13 },
  segment: { display: 'flex', padding: 2, gap: 2, borderRadius: 7, background: S.line },
  seg: { all: 'unset', cursor: 'pointer', padding: '3px 10px', borderRadius: 5, fontSize: 12, fontWeight: 600, color: S.muted },
  segOn: { background: S.field, color: S.fg },
  frames: { flex: 1, minHeight: 0, display: 'flex', gap: 16, padding: 16, overflow: 'hidden' },
  frameCol: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  frameLabel: { ...labelStyle, display: 'flex', justifyContent: 'space-between' },
  frameBox: { flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', background: S.field, boxShadow: '0 1px 3px rgba(23, 26, 32, 0.12), 0 8px 24px rgba(23, 26, 32, 0.08)' },
  // The requested version, at a glance: a blue label and ring (blue isn't one of the change colors).
  requestedLabel: { color: S.blue },
  requestedBox: { outline: `2px solid ${S.blue}`, outlineOffset: 3 },
  frame: { border: 0, transformOrigin: '0 0', position: 'absolute', top: 0, left: 0, background: S.field },
  unmarked: { fontSize: 12, color: S.amber }
});

/** A frame of the page as the site renders a version, scaled from desktop width to fit. */
function VersionFrame({ src, title, onLoad, frameRef, className }: { src: string; title: string; onLoad: () => void; frameRef: React.RefObject<HTMLIFrameElement>; className: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);
  React.useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const update = () => setScale(Math.min(1, el.clientWidth / DESKTOP));
    update();
    const RO = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    const ro = RO ? new RO(update) : null;
    if (ro) ro.observe(el);
    return () => { if (ro) ro.disconnect(); };
  }, []);
  return (
    <div ref={box} style={{ position: 'absolute', inset: 0 }}>
      <iframe
        ref={frameRef}
        title={title}
        src={src}
        className={className}
        onLoad={onLoad}
        style={{ width: DESKTOP, height: `${100 / scale}%`, transform: `scale(${scale})` }}
      />
    </div>
  );
}

/**
 * Reviewing a publish request: what changed since the live version, marked on
 * the page itself (before and after), the author's note, then approve or decline
 * with a reason. A page that isn't live (new, or withdrawn) has nothing to compare
 * with, so it's shown whole, with its sections listed instead of marked as added.
 */
const ReviewPage: React.FC = () => {
  const c = useStyles();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data, loading, error } = useQuery(REVIEW_PAGE, { variables: { id }, fetchPolicy: 'network-only' });
  const page = data && data.page;
  const requested = page && page.state === 'UNDER_REVIEW' ? page.commitedVersion : null;
  const after = useQuery<{ pageVersion: Version }>(REVIEW_VERSION, { variables: { pageId: id, versionId: requested }, skip: !requested });
  const before = useQuery<{ pageVersion: Version }>(REVIEW_VERSION, { variables: { pageId: id, versionId: page && page.publishedVersion }, skip: !page || !page.publishedVersion });
  const [approve] = useMutation(APPROVE, { refetchQueries: REFETCH });
  const [decline] = useMutation(DECLINE, { refetchQueries: REFETCH });
  const [view, setView] = useState<View>('side');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [unmarked, setUnmarked] = useState<string[]>([]);
  const afterFrame = useRef<HTMLIFrameElement>(null);
  const beforeFrame = useRef<HTMLIFrameElement>(null);

  const presets = (data && data.widgetPresets) || [];
  const nameOf = (comp: DiffComponent) => describeComponent(comp, presets, widgetSchemas as Record<string, { title?: string }>).title;
  const afterVersion = after.data && after.data.pageVersion;
  const beforeVersion = before.data && before.data.pageVersion;
  // A version whose body can't be loaded (an error, or none stored): say so instead of "Loading…" forever.
  const afterMissing = !!requested && !after.loading && (!!after.error || (!!after.data && !afterVersion));
  const beforeMissing = !!page && !!page.publishedVersion && !before.loading && (!!before.error || (!!before.data && !beforeVersion));
  const diff = useMemo(() => (afterVersion && (beforeVersion || !page.publishedVersion || beforeMissing)
    ? diffPages(beforeVersion ? { slots: beforeVersion.slots, template: beforeVersion.template } : null, { slots: afterVersion.slots, template: afterVersion.template })
    : null), [afterVersion, beforeVersion, beforeMissing, page]);
  const request = ((data && data.pageHistory) || []).find((h: { kind: string; version: string | null }) => h.kind === 'review-requested' && h.version === requested);
  // Not live (new, or withdrawn): every section would read "Added", so the page is reviewed as a whole.
  const isNew = !!page && !page.publishedVersion;

  // Outline the changed sections inside each frame (a page that isn't live has nothing marked).
  const label = (ch: SectionChange) => `${STATUS[ch.status].label} · ${nameOf(ch.component)}`;
  const changesIn = (statuses: SectionStatus[]) => (diff && !isNew ? diff.sections.filter(s => statuses.includes(s.status)) : []);
  const afterCss = afterVersion ? marksCss(changesIn(AFTER), afterVersion, label) : '';
  const beforeCss = beforeVersion ? marksCss(changesIn(BEFORE), beforeVersion, label) : '';
  // Watches for each frame's page to start arriving, then adds its marks once.
  useEffect(() => {
    if (!afterCss && !beforeCss) return undefined;
    const timer = window.setInterval(() => {
      const done = [!afterCss || injectMarks(afterFrame.current, afterCss), !beforeCss || injectMarks(beforeFrame.current, beforeCss)];
      if (done.every(Boolean)) window.clearInterval(timer);
    }, 50);
    return () => window.clearInterval(timer);
  }, [afterCss, beforeCss]);

  // Once a frame has loaded: its marks again (in case the page reloaded), and the slots whose sections
  // couldn't be lined up, so the list can say they aren't marked.
  const loaded = (frame: HTMLIFrameElement | null, version: Version | undefined, css: string, statuses: SectionStatus[]) => {
    const doc = frame && frame.contentDocument;
    // Some browsers fire a load for the frame's blank page before the preview: nothing to check yet.
    if (!doc || doc.location.href === 'about:blank') return;
    if (css) injectMarks(frame, css);
    const skipped = changesIn(statuses).filter(ch => !sectionIn(frame, version, ch.slotId, ch.index)).map(ch => SLOT_LABELS[ch.slotId] || ch.slotId);
    if (skipped.length) setUnmarked(prev => Array.from(new Set([...prev, ...skipped])));
  };
  const markAfter = () => loaded(afterFrame.current, afterVersion, afterCss, AFTER);
  const markBefore = () => loaded(beforeFrame.current, beforeVersion, beforeCss, BEFORE);

  const reveal = (change: SectionChange) => {
    const removed = change.status === 'removed';
    if (removed && view === 'after') setView('side');
    if (!removed && view === 'before') setView('side');
    const el = removed
      ? sectionIn(beforeFrame.current, beforeVersion, change.slotId, change.index)
      : sectionIn(afterFrame.current, afterVersion, change.slotId, change.index);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const act = async (kind: 'approve' | 'decline') => {
    setBusy(true);
    setActionError(null);
    try {
      const r = kind === 'approve'
        ? await approve({ variables: { id, version: requested } })
        : await decline({ variables: { id, reason: reason.trim() || null, version: requested } });
      const response = r.data && (r.data.approvePublishPage || r.data.declinePublishPage);
      if (!response || response.status !== 200) throw new Error((response && response.message) || 'That didn’t go through.');
      dispatch(flashMessage(kind === 'approve' ? `Approved version ${requested} of ${page.title}.` : `Declined version ${requested} of ${page.title}.`));
      history.push('/admin/approvals');
    } catch (e) {
      setActionError((e as Error).message);
      setBusy(false);
    }
  };

  if (loading && !data) return <div style={{ padding: 32, fontFamily: S.ui }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, fontFamily: S.ui }}>Couldn’t load the page: {error.message}</div>;
  if (!page) return <div style={{ padding: 32, fontFamily: S.ui }}>Page not found.</div>;

  const src = (v: string) => `${page.path}?preview=1&v=${encodeURIComponent(v)}`;
  return (
    <div className={c.root} data-testid="review-page">
      <aside className={c.side}>
        <div className={c.sideBody}>
          <div>
            <a className={c.back} href="/admin/approvals">← Approvals</a>
            <h1 className={c.title}>{page.title}</h1>
            <div className={c.path}>{page.path}</div>
            {requested && (
              <div className={c.versions}>
                {isNew ? <span>Not live yet:</span> : <><span>v{page.publishedVersion} live</span><span aria-hidden>→</span></>}
                <strong>v{requested}</strong>
              </div>
            )}
          </div>
          {!requested && <p className={c.muted}>Nothing on this page is waiting for review.</p>}
          {requested && (
            <section>
              <div className={c.label}>Note from the author</div>
              <p className={c.note} data-testid="review-note">{(afterVersion && afterVersion.notes) || 'No note.'}</p>
              {request && <div className={c.meta}>Asked {moment(request.at).fromNow()}{request.who ? ` by ${request.who}` : ''}</div>}
            </section>
          )}
          {requested && (
            <section>
              <div className={c.label}>{isNew ? 'Sections on this page' : 'Changes since the live version'}</div>
              {isNew && <p className={c.muted} style={{ marginBottom: 8 }} data-testid="review-new-page">This page isn’t live, so there’s nothing to compare it with. Review the whole page.</p>}
              {(after.loading || before.loading) && <p className={c.muted}>{isNew ? 'Loading…' : 'Comparing…'}</p>}
              {diff && !diff.sections.length && !diff.settings.length && <p className={c.muted}>{isNew ? 'No sections yet.' : 'No section changes.'}</p>}
              {diff && (
                <ul className={c.list} data-testid="review-changes">
                  {diff.sections.map((ch, i) => (
                    <li key={`${ch.status}-${ch.slotId}-${ch.index}-${i}`}>
                      <button type="button" className={`${c.change} ${isNew ? c.plain : ''}`} data-status={ch.status} onClick={() => reveal(ch)}>
                        {!isNew && <span className={c.status} style={{ color: STATUS[ch.status].color }}><span className={c.dot} style={{ background: STATUS[ch.status].color }} />{STATUS[ch.status].label}</span>}
                        <span className={c.name}>{nameOf(ch.component)}</span>
                        <span className={c.where}>{SLOT_LABELS[ch.slotId] || ch.slotId}</span>
                      </button>
                    </li>
                  ))}
                  {diff.settings.map(s => <li key={s.field} className={c.muted}>Changed {s.label.toLowerCase()}</li>)}
                </ul>
              )}
              {!!unmarked.length && !isNew && <p className={c.unmarked} data-testid="review-unmarked">Sections in {unmarked.join(', ')} couldn’t be marked on the page; see the list.</p>}
            </section>
          )}
        </div>
        {requested && page.canUserApprove && (
          <div className={c.actions}>
            {page.canUserDecide && <label htmlFor="decline-reason" className={c.muted}>Reason (sent with a decline)</label>}
            {page.canUserDecide && <textarea id="decline-reason" className={c.textarea} rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="What needs to change" />}
            {actionError && <p className={c.error} role="alert">{actionError}</p>}
            <button type="button" className={c.primary} disabled={busy || !diff} onClick={() => act('approve')} data-testid="review-approve">Approve v{requested}</button>
            {page.canUserDecide
              ? <button type="button" className={c.danger} disabled={busy || !reason.trim()} onClick={() => act('decline')} data-testid="review-decline" title={reason.trim() ? '' : 'Add a reason first'}>Decline</button>
              : <p className={c.muted}>Declining needs the approver role for this page’s category.</p>}
          </div>
        )}
        {requested && !page.canUserApprove && <div className={c.actions}><p className={c.muted}>Approving needs the approver role for this page.</p></div>}
      </aside>

      <main className={c.main}>
        {requested && afterMissing && (
          <p className={c.error} role="alert" style={{ margin: 24 }} data-testid="review-load-error">
            Couldn’t load v{requested}{after.error ? `: ${after.error.message}` : ''}. Try reloading; it can still be approved from the Approvals list without the comparison.
          </p>
        )}
        {requested && !diff && !afterMissing && <p className={c.muted} style={{ padding: 24 }}>Loading the versions…</p>}
        {requested && diff && beforeMissing && (
          <p className={c.muted} style={{ padding: '12px 24px 0' }} data-testid="review-live-missing">
            The live version v{page.publishedVersion} couldn’t be loaded, so every section is shown as new.
          </p>
        )}
        {/* The frames mark changes when they load, so they wait for the comparison. */}
        {requested && diff && (
          <>
            <div className={c.bar}>
              {!isNew && (
                <div className={c.segment} role="group" aria-label="Show">
                  {(['side', 'after', 'before'] as View[]).map(v => (
                    <button key={v} type="button" className={`${c.seg} ${view === v ? c.segOn : ''}`} aria-pressed={view === v} onClick={() => setView(v)}>
                      {v === 'side' ? 'Side by side' : v === 'after' ? `v${requested}` : 'Live'}
                    </button>
                  ))}
                </div>
              )}
              <span className={c.muted}>{isNew ? `Rendered at ${DESKTOP}px, as the site will render it.` : `Rendered at ${DESKTOP}px, as the site renders each version.`}</span>
            </div>
            <div className={c.frames}>
              {page.publishedVersion && (
                <div className={c.frameCol} style={view === 'after' ? { display: 'none' } : undefined}>
                  <div className={c.frameLabel}><span>Live</span><span>v{page.publishedVersion}</span></div>
                  <div className={c.frameBox}><VersionFrame src={src(page.publishedVersion)} title="Live version" frameRef={beforeFrame} onLoad={markBefore} className={c.frame} /></div>
                </div>
              )}
              <div className={c.frameCol} style={view === 'before' ? { display: 'none' } : undefined}>
                <div className={`${c.frameLabel} ${isNew ? '' : c.requestedLabel}`}><span>Requested</span><span>v{requested}</span></div>
                <div className={`${c.frameBox} ${isNew ? '' : c.requestedBox}`}><VersionFrame src={src(requested)} title="Requested version" frameRef={afterFrame} onLoad={markAfter} className={c.frame} /></div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ReviewPage;
